export const tabs = () => ({
  // Initialize tabs component
  init() {
    console.log('Tabs component initialized');
    this.setupKeyboardShortcuts();
    this.setupAuthRoleListener();
  },
  
  // Current active tab
  get activeTab() {
    return this.$store.app.ui.activeTab;
  },
  
  // Available tabs configuration
  tabsConfig: [
    { id: 'missions', label: 'Misiones', key: '1', icon: '⚔️' },
    { id: 'inventory', label: 'Inventario', key: '2', icon: '🎒' },
    { id: 'travel', label: 'Viajes', key: '3', icon: '🗺️' },
    { id: 'consume', label: 'Consumo', key: '4', icon: '🍖' },
    { id: 'rest', label: 'Descanso', key: '5', icon: '🛏️' },
    { id: 'shop', label: 'Tienda', key: '6', icon: '🛒' },
    { id: 'crafting', label: 'Crafteo', key: '7', icon: '🔨' },
    { id: 'house', label: 'Casa', key: '8', icon: '🏠' },
    { id: 'rewards', label: 'Recompensas', key: '9', icon: '🎁' }
  ],
  
  // Set active tab
  setActive(tabId) {
    const store = this.$store.app;
    
    // Check if tab change is allowed
    if (!this.canChangeTab(tabId)) {
      store.addNotification('No puedes cambiar de pestaña ahora', 'warning');
      return;
    }
    
    // Store previous tab for navigation history
    const previousTab = store.ui.activeTab;
    
    // Update active tab
    store.setActiveTab(tabId);
    
    // Track analytics event
    this.trackTabChange(tabId, previousTab);
    
    // Lazy load tab-specific resources
    this.loadTabResources(tabId);
    
    console.log(`Switched to tab: ${tabId}`);
  },
  
  // Check if tab change is allowed (some activities may block tab switching)
  canChangeTab(tabId) {
    const store = this.$store.app;
    
    // Block if player is KO'd
    if (store.player.health <= 0) {
      return false;
    }
    
    // Block if in critical mission phase
    if (store.timers.mission && store.missions.active?.critical) {
      return false;
    }
    
    // Block if traveling
    if (store.timers.travel) {
      return tabId === 'travel'; // Only allow travel tab when traveling
    }
    
    return true;
  },
  
  // Setup keyboard shortcuts for tabs
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (event) => {
      // Only trigger if no input is focused and not holding modifiers
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey
      ) {
        return;
      }
      
      // Map number keys to tabs
      const keyNumber = parseInt(event.key);
      if (keyNumber >= 1 && keyNumber <= 9) {
        const tab = this.tabsConfig[keyNumber - 1];
        if (tab) {
          event.preventDefault();
          this.setActive(tab.id);
        }
      }
    });
  },
  
  // Lazy load resources for specific tabs
  async loadTabResources(tabId) {
    switch (tabId) {
      case 'economy':
        // Load Chart.js for economy charts
        try {
          const { initEconomyChart } = await import('../features/economy-chart.js');
          await initEconomyChart();
        } catch (error) {
          console.warn('Failed to load economy chart:', error);
        }
        break;
        
      case 'inventory':
        // Preload sound effects for inventory actions
        try {
          const Howler = await import('howler');
          // Setup inventory sounds
        } catch (error) {
          console.warn('Failed to load audio library:', error);
        }
        break;
        
      case 'missions':
        // Load mission-specific data
        try {
          const { loadMissionData } = await import('../features/missions.js');
          await loadMissionData();
        } catch (error) {
          console.warn('Failed to load mission data:', error);
        }
        break;
    }
  },
  
  // Track tab changes for analytics
  trackTabChange(newTab, previousTab) {
    try {
      // Track with Fathom Analytics (when enabled)
      if (window.fathom) {
        window.fathom.trackGoal('TAB_CHANGE', 0);
      }
      
      // Custom event for other analytics
      window.dispatchEvent(new CustomEvent('tab-change', {
        detail: { newTab, previousTab, timestamp: Date.now() }
      }));
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  },
  
  // Get tab display info
  getTabInfo(tabId) {
    return this.tabsConfig.find(tab => tab.id === tabId) || { label: tabId, icon: '❓' };
  },
  
  // Check if tab has notifications/badges
  hasNotifications(tabId) {
    const store = this.$store.app;
    
    switch (tabId) {
      case 'missions':
        return store.missions.available.length > 0 && !store.missions.active;
      case 'inventory':
        return store.inventory.currentWeight > store.inventory.maxWeight * 0.9; // Near full
      case 'rewards':
        return store.missions.completed.some(m => !m.claimed);
      default:
        return false;
    }
  },
  
  // Get notification count for tab
  getNotificationCount(tabId) {
    const store = this.$store.app;
    
    switch (tabId) {
      case 'missions':
        return store.missions.available.length;
      case 'rewards':
        return store.missions.completed.filter(m => !m.claimed).length;
      default:
        return 0;
    }
  },
  
  // Handle tab button styling
  getTabClasses(tabId) {
    const classes = ['tab-button'];
    
    if (this.activeTab === tabId) {
      classes.push('active');
    }
    
    if (this.hasNotifications(tabId)) {
      classes.push('has-notifications');
    }
    
    if (!this.canChangeTab(tabId)) {
      classes.push('disabled');
    }
    
    return classes.join(' ');
  },
  
  // Navigation history for back/forward
  navigationHistory: [],
  currentHistoryIndex: -1,
  
  // Add to navigation history
  addToHistory(tabId) {
    // Remove any forward history
    this.navigationHistory = this.navigationHistory.slice(0, this.currentHistoryIndex + 1);
    
    // Add new entry
    this.navigationHistory.push(tabId);
    this.currentHistoryIndex = this.navigationHistory.length - 1;
    
    // Limit history size
    if (this.navigationHistory.length > 10) {
      this.navigationHistory.shift();
      this.currentHistoryIndex--;
    }
  },
  
  // Navigate back in history
  goBack() {
    if (this.currentHistoryIndex > 0) {
      this.currentHistoryIndex--;
      const previousTab = this.navigationHistory[this.currentHistoryIndex];
      this.setActive(previousTab);
    }
  },
  
  // Navigate forward in history
  goForward() {
    if (this.currentHistoryIndex < this.navigationHistory.length - 1) {
      this.currentHistoryIndex++;
      const nextTab = this.navigationHistory[this.currentHistoryIndex];
      this.setActive(nextTab);
    }
  },
  
  // Setup auth role change listener for reactive admin tab visibility
  setupAuthRoleListener() {
    window.addEventListener('auth:role', (event) => {
      console.log('[TABS] Auth role changed:', event.detail);
      
      // Force Alpine.js to re-evaluate x-show directives for admin button
      this.$nextTick(() => {
        // This helps ensure the admin button appears/disappears reactively
        const adminButton = document.querySelector('button[aria-label="Abrir panel de administración"]');
        if (adminButton) {
          // Trigger a re-evaluation by temporarily modifying a data attribute
          adminButton.setAttribute('data-role-updated', Date.now());
        }
      });
    });
  }
});